import React from 'react';
import { css } from '@emotion/css';
import ResponseTemplateTypeModel from '../../../models/features/api/MediaResponses/ResponseTemplateTypeModel';
import UpdateMediaResponseRequest from '../../../models/features/api/MediaResponses/UpdateMediaResponseRequest';
import HorizontalSeparator from '../../../components/structure/HorizontalSeparator';
import { dark_grey_blue, greeny_blue, green_1, faded_red } from '../../../constants/colors';
import SelectField from '../../../components/forms/SelectField';
import { ResponseFieldCard } from './ResponseFieldCard';
import IconSelectField, { IconOption } from '../../../components/forms/IconSelectField';
import ResponseTemplateSelector from './ResponseTemplateSelector';
import UpdateBulkMediaResponseContainerRequest from '../../../models/features/api/MediaResponses/UpdateBulkMediaResponseContainerRequest';
import RadioGroup, { RadioOption } from '../../../components/forms/RadioGroup';
import MediaItemModel from '../../../models/media/MediaItemModel';
import MediaType from '../../../models/media/MediaTypes';
import FormFieldsContainer from '../../../components/structure/FormFieldsContainer';
import TextAreaField from '../../../components/forms/TextAreaField';
import _ from 'lodash';
import DeviceTargetModel from '../../../models/features/api/MediaResponses/DeviceTargetModel';
import CompatabilityViewer from './CompatabilityViewer';
import Button from '../../../components/general/Button';
import PageError from '../../../components/general/PageError';
import SsmlField from '../../../components/forms/SsmlField/SsmlField';
import DefaultMedia from '../../../models/features/api/DefaultMedia';
import ResponseBuilderContainer from './ResponseBuilderContainer';
const readoutIcon = require('../../../content/images/media-icons/Readout-Dark.svg');
const audioIcon = require('../../../content/images/media-icons/Audio-dark.svg')
const textIcon = require('../../../content/images/media-icons/Text-dark.svg');
const videoIcon = require('../../../content/images/media-icons/Video-dark.svg');
const noVideoIcon = require('../../../content/images/media-icons/Video-None-dark.svg');
const imageIcon = require('../../../content/images/media-icons/Image-dark.svg');
const noImageIcon = require('../../../content/images/media-icons/Image-None-dark.svg');
const resetIcon = require('../../../content/images/reset-blue.svg');
const customIcon = require('../../../content/images/hammer-wench.svg');

interface ResponseBuilderProps {
    audioItem?: MediaItemModel
    targets: DeviceTargetModel[],
    onChange?: (container: UpdateBulkMediaResponseContainerRequest) => void
    onDefaultsChange?: (media: DefaultMedia) => void
    container: UpdateBulkMediaResponseContainerRequest
    deviceType: string
    mediaItems: MediaItemModel[]
    applicationId: string
}
interface ResponseBuilderState {
    deviceTarget: DeviceTargetModel
    customResponse?: UpdateMediaResponseRequest
    validationErrors?: string[]
    successMessage?: string
    hasCustomChanges: boolean
}

class AudioResponseBuilder extends React.Component<ResponseBuilderProps, ResponseBuilderState> {
    componentWillMount() {
        this.setState({
            ...this.state,
            deviceTarget: this.props.targets[0],
            customResponse: this.getCurrentResponse(this.props.targets[0].id),
            validationErrors: null,
            successMessage: null,
            hasCustomChanges: false
        })
    }

    componentDidUpdate(prevProps: ResponseBuilderProps) {
        if (prevProps.deviceType != this.props.deviceType) {
            this.setState({
                ...this.state,
                deviceTarget: this.props.targets[0],
                customResponse: this.getCurrentResponse(this.props.targets[0].id)
            })
        }
        else if (this.props.audioItem != prevProps.audioItem
            || this.props.container.responses.length != prevProps.container.responses.length) {
            this.setState({
                ...this.state,
                customResponse: this.getCurrentResponse(this.state.deviceTarget.id)
            })
        }
    }

    private getCurrentResponse(deviceTargetId?: string): UpdateMediaResponseRequest {
        if (!deviceTargetId) deviceTargetId = this.state.deviceTarget.id
        const existingResponse = this.props.container.responses.find(r => r.deviceTargetId == deviceTargetId);
        if (existingResponse) {
            return existingResponse;
        }
        else {
            return this.buildAudioResponseFromDefault(deviceTargetId);
        }
    }

    private buildAudioResponseFromDefault(deviceTargetId?: string): UpdateMediaResponseRequest {
        if (!deviceTargetId) deviceTargetId = this.state.deviceTarget.id
        const target = this.props.targets.find(t => t.id == deviceTargetId);
        if (!target) return null;

        // if audio and target supports, set that template
        if (this.props.audioItem && target.supportsRichAudio) {
            const template = target.responseTemplates.find(t => t.usesAudio);
            if (template) {
                return {
                    audioItemId: this.props.audioItem.id,
                    responseTemplateTypeId: template.id,
                    applicationId: this.props.applicationId,
                    deviceTargetId: deviceTargetId
                }
            }
        }

        const template = target.responseTemplates.find(t => t.usesReadout);
        if (!template) return;
        return {
            responseTemplateTypeId: template.id,
            applicationId: this.props.applicationId,
            deviceTargetId: deviceTargetId
        }
    }

    handleTargetChange(selection) {
        const target = this.props.targets.find(t => t.id == selection.value);
        this.setState({
            ...this.state,
            deviceTarget: target,
            customResponse: this.getCurrentResponse(target.id),
            hasCustomChanges: false,
            validationErrors: null,
            successMessage: null
        });
    }

    handleDefaultAudioChange(option: RadioOption) {
        const propValues = { ...this.props }
        propValues.audioItem = option.value == "default" ? null : this.props.mediaItems.find(m => m.id == option.value);
        this.props.onDefaultsChange(propValues);
    }

    hasCustomResponse(deviceTargetId: string) {
        const isCustom = this.props.container.responses.some(r => r.deviceTargetId == deviceTargetId);
        return isCustom;
    }

    removeTargetResponse(deviceTargetId: string) {
        const container = this.props.container;
        _.remove(container.responses, r => r.deviceTargetId == deviceTargetId);
        this.props.onChange(container);
        // reset values
        this.setState({
            ...this.state,
            customResponse: this.getCurrentResponse(this.state.deviceTarget.id)
        })
    }

    handleSpeakerAudioSourceChange(option: RadioOption) {
        if (option.value == 'default') {
            this.removeTargetResponse(this.state.deviceTarget.id);
            var existingResponse = this.state.customResponse;

            if (existingResponse) {
                existingResponse.readoutOverride = null;
                existingResponse.audioItemId = null;
                this.setState({
                    ...this.state,
                    customResponse: existingResponse,
                    hasCustomChanges: false
                });
            }
            else {
                this.setState({
                    ...this.state,
                    hasCustomChanges: false
                });
            }
        }
        else if (option.value == 'custom') {
            var existingResponse = this.state.customResponse;
            if (existingResponse) {
                existingResponse.readoutOverride = '';
                existingResponse.audioItemId = null;
                this.setState({
                    ...this.state,
                    customResponse: existingResponse,
                    hasCustomChanges: true
                });
            }
        }
        else {
            var existingResponse = this.state.customResponse;
            if (existingResponse) {
                existingResponse.audioItemId = option.value;
                existingResponse.readoutOverride = null;
                this.setState({
                    ...this.state,
                    customResponse: existingResponse,
                    hasCustomChanges: true
                });
            }
        }
    }

    getMediaItems(type: MediaType) {
        return this.props.mediaItems.filter(m => m.mediaType == type);
    }

    handleRemoveCustomization() {
        this.removeTargetResponse(this.state.deviceTarget.id);
    }

    handleReadoutTextChange(ssml: string) {
        var existingResponse = this.state.customResponse;
        if (existingResponse) {
            existingResponse.readoutOverride = ssml;
            this.setState({
                ...this.state,
                customResponse: existingResponse,
                hasCustomChanges: true
            });
        }
    }

    handleUpdateResponse() {
        const validationErrors = this.verifyCustomResponse();
        if (validationErrors) {
            this.setState({
                ...this.state,
                validationErrors: [validationErrors]
            });
            return;
        }

        const container = this.props.container;
        const existingResponse = container.responses.find(r => r.deviceTargetId == this.state.deviceTarget.id);
        if (existingResponse && (this.state.customResponse.audioItemId != null || this.state.customResponse.readoutOverride != null)) {
            const index = container.responses.indexOf(existingResponse);
            container.responses[index] = this.state.customResponse;
        }
        else if (existingResponse) {
            // default set, remove the customization
            this.handleRemoveCustomization();
            return;
        }
        else if (this.state.customResponse.audioItemId != null || this.state.customResponse.readoutOverride != null){
            container.responses.push(this.state.customResponse);
        }

        this.props.onChange(container);
        this.setState({
            ...this.state,
            validationErrors: null,
            successMessage: "Saved",
            hasCustomChanges: false
        });

        // hide save message after delay
        setTimeout(() => {
            this.setState({
                ...this.state,
                successMessage: null
            })
        }, 3000);
    }

    private verifyCustomResponse(): string {
        const response = this.state.customResponse;
        if (!response) return "Invalid Response";
        const template = this.state.deviceTarget.responseTemplates.find(t => t.id == this.state.customResponse.responseTemplateTypeId);
        if (!template) return "Invalid template";

        if (template.requiresVideo && !response.videoItemId) return "This template requires a video";
        if (template.requiresAudio && !response.audioItemId) return "This template requires an audio file";
        if (template.requiresForegroundImage && !response.foregroundImageItemId) return "This template requires a foreground image";
        if (template.requiresBackgroundImage && !response.backgroundImageItemId) return "This template requires a background image";
        return null;
    }

    private getAudioRadioValue() 
    {
        if(this.state.customResponse.audioItemId)
            return this.state.customResponse.audioItemId;

        if(this.state.customResponse.readoutOverride != null)
            return 'custom';

        return 'default';
    }

    render() {
        return (
            <ResponseBuilderContainer>
                <div>
                    <h4>Default Components</h4>
                    <p>This sets the underlying components used in a response. We will apply a best-fit solution for each device based on their respective capabilities. </p>

                    <div>
                        <ResponseFieldCard title="Audio Source">
                            <RadioGroup
                                options={[...this.props.mediaItems.filter(m => m.mediaType == "AudioFile").map(item => ({ icon: audioIcon, value: item.id, label: item.name })), { icon: readoutIcon, value: 'default', label: 'Device Readout' }]}
                                onChange={this.handleDefaultAudioChange.bind(this)}
                                value={this.props.audioItem ? this.props.audioItem.id : 'default'} />
                        </ResponseFieldCard>
                    </div>

                </div>
                <h4>Custom Overrides</h4>
                <p>Customizations made here will apply to the specific device-type and override the default settings</p>

                {this.props.targets.length > 1 ?
                    <div className="type-selector">
                        <IconSelectField name="deviceType"
                            options={_.sortBy(this.props.targets.map(t => ({
                                value: t.id,
                                icon: t.iconUrl,
                                label: t.displayName,
                                rightIcon: this.hasCustomResponse(t.id) ? customIcon : null,
                                rightText: this.hasCustomResponse(t.id) ? "CUSTOM" : null
                            } as IconOption)), o => o.label)}
                            clearable={false}
                            label="Device-Type:"
                            value={this.state.deviceTarget.id}
                            onBlur={() => { }}
                            onChange={this.handleTargetChange.bind(this)} />
                    </div>
                    : null}
                <div>

                    {this.hasCustomResponse(this.state.deviceTarget.id) ?
                        <Button themes={['hollow', 'end-tight']} type="button" onClick={this.handleRemoveCustomization.bind(this)} additionalClasses="reset-button" text="Reset to default" icon={resetIcon} />
                        : null}
                    <ResponseFieldCard title="Audio Source">
                        <RadioGroup
                            options={[{ icon: readoutIcon, value: 'default', label: 'Default Readout' },
                            ...this.props.mediaItems.filter(m => m.mediaType == "AudioFile")
                                .map(item => ({ icon: audioIcon, value: item.id, label: item.name })),
                            { icon: readoutIcon, value: 'custom', label: 'Custom Readout' }]}
                            onChange={this.handleSpeakerAudioSourceChange.bind(this)}
                            value={this.getAudioRadioValue()} />
                        {this.state.customResponse && this.state.customResponse.readoutOverride != null ?
                            <SsmlField name="readoutOverride" placeholder="What do you want the assistant to say?" value={this.state.customResponse.readoutOverride} onChange={this.handleReadoutTextChange.bind(this)} onBlur={() => { }} applicationId={this.props.applicationId} />
                            : null}
                        <div className="update-container">
                            <Button themes={['primary-small']} disabled={!this.state.hasCustomChanges} type="button" onClick={this.handleUpdateResponse.bind(this)} additionalClasses="update-button" text="Update" />
                            <p className="error-label">{this.state.validationErrors ? this.state.validationErrors.join(',') : null}</p>
                            <p className="success-label">{this.state.successMessage}</p>
                        </div>
                    </ResponseFieldCard>
                </div>
            </ResponseBuilderContainer>
        )
    }
}

export default AudioResponseBuilder